// ============================================================================
// Spain's three indirect-tax systems and the exemption causes.
// ----------------------------------------------------------------------------
// Península y Baleares charge IVA; the Canary Islands charge IGIC and Ceuta /
// Melilla charge IPSI — different names, rates and laws, same arithmetic. The
// account picks its system in Configuración › Negocio (profiles.tax_system);
// the Spain fiscal adapter is then re-labelled through adapterForTaxSystem():
// tax name, default rate and rate list change, the maths in lib/taxes.js does
// not. Nothing here runs for an account that keeps IVA (or is not Spanish).
//
// Exemption causes: a document at 0 % states WHY it carries no tax
// (Verifactu's E1–E6 / N1–N2 codes, in Billin's naming). The code is stored
// on the document and printed as a legend on the PDF.
// ============================================================================

export const TAX_SYSTEMS = {
  iva:  { key: 'iva',  label: 'IVA',  law: 'Ley 37/1992', defaultRate: 21, rates: [21, 10, 4, 0], recargoEquiv: true },
  igic: { key: 'igic', label: 'IGIC', law: 'Ley 20/1991', defaultRate: 7,  rates: [7, 3, 0, 9.5, 15, 20], recargoEquiv: false },
  ipsi: { key: 'ipsi', label: 'IPSI', law: 'Ley 8/1991',  defaultRate: 10, rates: [10, 8, 4, 2, 1, 0.5, 0], recargoEquiv: false },
};
export const TAX_SYSTEM_KEYS = ['iva', 'igic', 'ipsi'];

export const normaliseTaxSystem = (key) => {
  const k = String(key || '').toLowerCase();
  return TAX_SYSTEMS[k] ? k : 'iva';
};

/** Human name for the settings picker. */
export function taxSystemName(key, t) {
  const k = normaliseTaxSystem(key);
  return t ? t(`taxSys.name_${k}`, { defaultValue: TAX_SYSTEMS[k].label }) : TAX_SYSTEMS[k].label;
}

// Why a document carries no tax. Codes follow the AEAT / Verifactu vocabulary.
export const EXEMPTION_CAUSES = ['E1', 'E2', 'E3', 'E4', 'E5', 'E6', 'OT', 'RL', 'VT', 'IE'];

/** Short label for the editor select: "E1 · Exenta art. 20 (operaciones interiores)". */
export function exemptionLabel(code, t) {
  return `${code} · ${t(`taxSys.exl_${code}`, { defaultValue: code })}`;
}

/** Legend printed on the PDF; '' when no cause is set. */
export function exemptionLegend(code, taxKey, t) {
  if (!code || !EXEMPTION_CAUSES.includes(code)) return '';
  const sys = TAX_SYSTEMS[normaliseTaxSystem(taxKey)];
  return t(`taxSys.ex_${code}`, { tax: sys.label, law: sys.law, defaultValue: code });
}

/** Any article line at 0 % (title rows and empty lines ignored)? */
export function hasZeroRateLines(lines) {
  return (lines || []).some((l) => l && l.lineType !== 'title' && l.line_type !== 'title'
    && Number(l.taxRate ?? l.tax_rate ?? 0) === 0
    && (Number(l.quantity) || 0) !== 0
    && ((l.description || '').trim() || l.product_id));
}

const derived = new Map();

/**
 * The Spain adapter re-labelled for IGIC / IPSI. Returns `base` itself for
 * IVA (or for any other country), so the historic path is untouched.
 */
export function adapterForTaxSystem(base, key) {
  const k = normaliseTaxSystem(key);
  if (!base || k === 'iva' || base.countryCode !== 'ES') return base;
  if (derived.has(k)) return derived.get(k);
  const sys = TAX_SYSTEMS[k];
  const out = {
    ...base,
    taxSystem: k,
    meta: { ...base.meta, taxLabel: sys.label },
    // Recargo de equivalencia is an IVA-only regime.
    taxFeatures: { ...base.taxFeatures, recargoEquiv: false },
    defaultTaxRate: sys.defaultRate,
    taxRates: sys.rates.map((r) => ({ value: String(r), label: `${r}% ${sys.label}` })),
    computeTotals(lines, opts) {
      return { ...base.computeTotals(lines, opts), taxLabel: sys.label };
    },
  };
  derived.set(k, out);
  return out;
}
